import { lazy, type ComponentType } from "react";

// Pantallas que se cargan aparte (para que la primera carga quede dentro de su límite) sin pagar la espera de Suspense:
// React 19 retiene 300 ms la salida de un límite de Suspense después de que llega su código (agrupa las revelaciones), y
// esa espera, más los pedidos de datos que la pantalla hace al montarse, entraba entera en cada acción que la abre. Una
// pantalla ya cargada se dibuja directo, sin Suspense. Al arrancar se pide solo el código de la pantalla que se abre
// (preloadScreensFor) y el resto cuando el portal queda en reposo (preloadScreensWhenIdle): traer todo de una vez
// atrasaba la primera carga de las pantallas que no lo usan.

type Entry = { route: RegExp | null; preload: () => Promise<unknown> };
const entries: Entry[] = [];

/**
 * Componente de pantalla cargado aparte; `preload` trae su código sin dibujarla. `route` (dirección en minúsculas, sin
 * barra final) indica dónde se usa, para pedir su código al arrancar en esa dirección.
 */
export function lazyScreen<P extends object>(
  load: () => Promise<ComponentType<P>>,
  route: RegExp | null = null,
): ComponentType<P> & { preload: () => Promise<ComponentType<P>> } {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;
  const preload = () =>
    (pending ??= load().then(
      (component) => (loaded = component),
      (error: unknown) => {
        pending = null; // un fallo de red no deja la pantalla sin poder abrirse después
        throw error;
      },
    ));
  const Lazy = lazy(async () => ({ default: await preload() })) as unknown as ComponentType<P>;
  /** Va dentro de un Suspense (el de quien la usa decide qué se ve mientras llega el código). */
  function Screen(props: P) {
    const Ready = loaded;
    return Ready ? <Ready {...props} /> : <Lazy {...props} />;
  }
  entries.push({ route, preload });
  return Object.assign(Screen, { preload });
}

/** Pide ya, en paralelo con la sesión y el shell, el código de las pantallas que usa esta dirección. */
export function preloadScreensFor(path: string): void {
  const clean = path.replace(/\/+$/, "").toLowerCase();
  for (const entry of entries) if (entry.route?.test(clean)) void entry.preload().catch(() => undefined);
}

/** Precarga el código de todas las pantallas aparte cuando el navegador queda libre (después de la primera pintura). */
export function preloadScreensWhenIdle(): () => void {
  const run = () => {
    for (const entry of entries) void entry.preload().catch(() => undefined);
  };
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(run, { timeout: 4000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(run, 1500);
  return () => window.clearTimeout(id);
}
